import {isValidElement, ReactNode} from 'react';

export type TocHeading = {
  id: string;
  text: string;
  level: 2 | 3;
};

// Height of the fixed app bar plus breathing room — anchors and the
// scroll-spy band must clear it or headings land hidden underneath.
export const HEADING_SCROLL_OFFSET = 96;

// GitHub-style anchor slugs: lowercase, drop punctuation, spaces → hyphens.
// Must stay a pure function of the heading text — ArticleView derives ids
// from rendered children while extractHeadings derives them from raw
// markdown, and both must agree.
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-');
}

// Reduce inline markdown (bold, code, links, wiki-links) to its visible text
// so slugs match what react-markdown renders.
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[\[([^|\]]+)\|([^\]]+)\]\]/g, '$2')
    .replace(/\[\[([^\]]+)\]\]/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
}

// List the h2/h3 headings of a markdown article, in document order.
// h1 is the article title and stays out of the table of contents.
export function extractHeadings(markdown: string): TocHeading[] {
  const headings: TocHeading[] = [];
  let inCodeFence = false;

  for (const line of markdown.split('\n')) {
    if (/^\s*(```|~~~)/.test(line)) {
      inCodeFence = !inCodeFence;
      continue;
    }
    if (inCodeFence) {
      continue;
    }
    const match = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!match) {
      continue;
    }
    const text = stripInlineMarkdown(match[2]);
    if (!text) {
      continue;
    }
    headings.push({
      id: slugifyHeading(text),
      text,
      level: match[1].length as 2 | 3,
    });
  }

  return headings;
}

// Text content of rendered markdown children, for deriving heading ids.
export function flattenNodeText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(flattenNodeText).join('');
  }
  if (isValidElement(node)) {
    return flattenNodeText((node.props as {children?: ReactNode}).children);
  }
  return '';
}

export function scrollToHeading(id: string) {
  const element = document.getElementById(id);
  if (!element) {
    return;
  }
  element.scrollIntoView({behavior: 'smooth', block: 'start'});
  window.history.replaceState(null, '', `#${id}`);
}
